<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment-timezone';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  name: { type: String, default: '' },
  description: { type: String, default: '' },
  publicContent: { type: String, default: '' },
  privateContent: { type: String, default: '' },
  referenceContent: { type: String, default: '' },
  hashtags: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
  startsAt: { type: String, default: '' },
  endsAt: { type: String, default: '' },
  timezone: { type: String, default: null },
  status: { type: String, default: 'inactive' },
});

const emit = defineEmits(['edit', 'delete', 'toggleStatus']);

const { t } = useI18n();

const isActive = computed(() => props.status === 'active');

const isScheduledInFuture = computed(
  () => isActive.value && props.startsAt && moment().isBefore(props.startsAt)
);

const isEnded = computed(
  () => isActive.value && props.endsAt && moment().isAfter(props.endsAt)
);

const statusConfig = computed(() => {
  const configs = {
    inactive: {
      label: t('SMART_CAMPAIGNS.CARD.STATUS.INACTIVE'),
      badgeClass: 'text-n-slate-11 bg-n-alpha-2',
      chipClass: 'text-n-slate-11 bg-n-alpha-2',
    },
    active: {
      label: t('SMART_CAMPAIGNS.CARD.STATUS.ACTIVE'),
      badgeClass: 'text-n-teal-11 bg-n-teal-3',
      chipClass: 'text-n-teal-11 bg-n-teal-3',
    },
  };
  return configs[props.status] ?? configs.inactive;
});

const DEFAULT_TIMEZONE = 'Asia/Saigon';

const formatInTimezone = (date, tz) => {
  if (!date) return '';
  return `${moment(date).tz(tz).format('MMM D, YYYY HH:mm')} (${tz})`;
};

const formatDateOriginal = date => {
  if (!date) return '';
  const tz = props.timezone || DEFAULT_TIMEZONE;
  return formatInTimezone(date, tz);
};

const formatDateGMT7 = date => {
  if (!date) return '';
  return formatInTimezone(date, DEFAULT_TIMEZONE);
};
</script>

<template>
  <CardLayout>
    <div class="flex items-start justify-between gap-3">
      <div class="flex items-center gap-2 min-w-0">
        <span class="text-base font-medium text-n-slate-12 truncate">
          {{ name }}
        </span>
        <span
          class="shrink-0 text-xs font-medium inline-flex items-center h-5 px-2 rounded-md"
          :class="statusConfig.badgeClass"
        >
          {{ statusConfig.label }}
        </span>
      </div>
      <div class="flex items-center gap-1.5 shrink-0">
        <Switch :model-value="isActive" @click="emit('toggleStatus')" />
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-sliders-vertical"
          @click="emit('edit')"
        />
        <Button
          variant="faded"
          size="sm"
          color="ruby"
          icon="i-lucide-trash"
          @click="emit('delete')"
        />
      </div>
    </div>

    <div
      v-if="isScheduledInFuture"
      class="w-fit flex items-center gap-1.5 text-xs text-n-amber-11 bg-n-amber-3 px-3 py-1.5 rounded-lg"
    >
      <span class="i-lucide-clock size-3.5 shrink-0" />
      {{ t('SMART_CAMPAIGNS.CARD.SCHEDULED_WARNING') }}
    </div>
    <div
      v-if="isEnded"
      class="w-fit flex items-center gap-1.5 text-xs text-n-ruby-11 bg-n-ruby-3 px-3 py-1.5 rounded-lg"
    >
      <span class="i-lucide-clock-alert size-3.5 shrink-0" />
      {{ t('SMART_CAMPAIGNS.CARD.ENDED_WARNING') }}
    </div>

    <p
      v-if="description"
      class="text-sm text-n-slate-11 line-clamp-2 m-0 leading-snug"
    >
      {{ description }}
    </p>

    <div class="grid grid-cols-3 gap-4">
      <div class="space-y-1 min-w-0">
        <p class="text-xs font-medium text-n-slate-10 m-0">
          {{ t('SMART_CAMPAIGNS.CARD.CONTENT.PUBLIC') }}
        </p>
        <p
          class="text-sm text-n-slate-11 line-clamp-3 leading-snug m-0 whitespace-pre-line"
        >
          {{ publicContent || '—' }}
        </p>
      </div>
      <div class="space-y-1 min-w-0">
        <p class="text-xs font-medium text-n-slate-10 m-0">
          {{ t('SMART_CAMPAIGNS.CARD.CONTENT.PRIVATE') }}
        </p>
        <p
          class="text-sm text-n-slate-11 line-clamp-3 leading-snug m-0 whitespace-pre-line"
        >
          {{ privateContent || '—' }}
        </p>
      </div>
      <div class="space-y-1 min-w-0">
        <p class="text-xs font-medium text-n-slate-10 m-0">
          {{ t('SMART_CAMPAIGNS.CARD.CONTENT.REFERENCE') }}
        </p>
        <p class="text-sm text-n-slate-11 line-clamp-3 leading-snug m-0">
          {{ referenceContent || '—' }}
        </p>
      </div>
    </div>

    <div v-if="inboxes.length" class="flex flex-wrap items-center gap-1.5">
      <span class="text-xs text-n-slate-10 whitespace-nowrap">
        {{ t('SMART_CAMPAIGNS.CARD.INBOXES') }}
      </span>
      <span
        v-for="inbox in inboxes"
        :key="inbox.id"
        class="text-xs font-medium px-2 py-0.5 rounded-md"
        :class="statusConfig.chipClass"
      >
        {{ inbox.name }}
      </span>
      <template v-if="hashtags.length">
        <span class="text-xs text-n-slate-10 whitespace-nowrap">
          {{ t('SMART_CAMPAIGNS.CARD.CONTAINING') }}
        </span>
        <span
          v-for="tag in hashtags"
          :key="tag"
          class="text-xs font-medium px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-12"
        >
          {{ tag }}
        </span>
      </template>
    </div>
    <div
      v-else-if="hashtags.length"
      class="flex flex-wrap items-center gap-1.5"
    >
      <span
        v-for="tag in hashtags"
        :key="tag"
        class="text-xs font-medium px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-12"
      >
        {{ tag }}
      </span>
    </div>

    <div v-if="startsAt || endsAt" class="flex items-center gap-1.5 flex-wrap">
      <span class="text-xs text-n-slate-10 whitespace-nowrap">
        {{ t('SMART_CAMPAIGNS.CARD.DURATION') }}
      </span>
      <div
        v-if="startsAt"
        class="flex flex-col text-xs font-medium px-2 py-0.5 rounded-md"
        :class="statusConfig.chipClass"
      >
        <span>{{ formatDateOriginal(startsAt) }}</span>
        <span
          v-if="timezone && timezone !== DEFAULT_TIMEZONE"
          class="text-n-slate-10 font-normal"
        >
          {{ formatDateGMT7(startsAt) }}
        </span>
      </div>
      <span
        v-if="startsAt && endsAt"
        class="i-lucide-arrow-right size-3 text-n-slate-10 shrink-0"
      />
      <div
        v-if="endsAt"
        class="flex flex-col text-xs font-medium px-2 py-0.5 rounded-md"
        :class="statusConfig.chipClass"
      >
        <span>{{ formatDateOriginal(endsAt) }}</span>
        <span
          v-if="timezone && timezone !== DEFAULT_TIMEZONE"
          class="text-n-slate-10 font-normal"
        >
          {{ formatDateGMT7(endsAt) }}
        </span>
      </div>
    </div>
  </CardLayout>
</template>
